/**
 * Support Page
 *
 * User-facing support page for creating and viewing tickets.
 * Shows ticket list or detail view based on selection.
 */

import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Loader2, RefreshCw, Headphones } from "lucide-react";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { NewTicketForm } from "../components/support/NewTicketForm";
import { TicketList } from "../components/support/TicketList";
import { TicketDetail } from "../components/support/TicketDetail";
import { useTickets } from "../hooks/useSupport";

type View = "list" | "new" | "detail";

export function SupportPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState<View>("list");
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const { data, isLoading, refetch } = useTickets(page);

  // Handle URL params for pre-filling (e.g., from processing failure)
  const reflectionId = searchParams.get("reflection_id");

  // Switch to new ticket view if reflection_id is in URL
  useEffect(() => {
    if (reflectionId) {
      setView("new");
    }
  }, [reflectionId]);

  const handleSelectTicket = useCallback((ticketId: string) => {
    setSelectedTicketId(ticketId);
    setView("detail");
  }, []);

  const handleBack = useCallback(() => {
    setSelectedTicketId(null);
    setView("list");
  }, []);

  const handleNewTicket = useCallback(() => {
    setView("new");
  }, []);

  const handleTicketCreated = useCallback(() => {
    // Clear the reflection_id from URL after ticket creation
    if (reflectionId) {
      setSearchParams({});
    }
    setView("list");
    refetch();
  }, [reflectionId, refetch, setSearchParams]);

  const handleCancelNew = useCallback(() => {
    if (reflectionId) {
      setSearchParams({});
    }
    setView("list");
  }, [reflectionId, setSearchParams]);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {view === "list" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Headphones className="w-6 h-6 text-primary" />
                <h1 className="text-2xl font-bold text-foreground">Support</h1>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => refetch()}
                  disabled={isLoading}
                  title="Refresh"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
                  />
                </Button>
                <Button onClick={handleNewTicket}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Ticket
                </Button>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg overflow-hidden">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <TicketList
                    tickets={data?.items || []}
                    onSelect={handleSelectTicket}
                  />

                  {/* Pagination */}
                  {data && data.total_pages > 1 && (
                    <div className="border-t border-border p-4 flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        Page {page} of {data.total_pages}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={page === 1}
                          onClick={() => setPage((p) => p - 1)}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={page === data.total_pages}
                          onClick={() => setPage((p) => p + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {view === "new" && (
          <>
            <h1 className="text-2xl font-bold text-foreground mb-6">
              Create Support Ticket
            </h1>
            <div className="bg-card border border-border rounded-lg p-6">
              <NewTicketForm
                reflectionId={reflectionId || undefined}
                onSuccess={handleTicketCreated}
                onCancel={handleCancelNew}
              />
            </div>
          </>
        )}

        {view === "detail" && selectedTicketId && (
          <TicketDetail ticketId={selectedTicketId} onBack={handleBack} />
        )}
      </div>
    </Layout>
  );
}
